// Pricing route (public storefront). Keep keys in sync with en/pricing.ts.
// Prices/gem counts come from the billing API (GET /api/billing/plans + /topups)
// — only marketing copy lives here.
//
// CREDIBILITY PASS (P06): the plan bullets described a different product.
// Removed, with the reason:
//   · "Eksport 4K" — the renderer outputs 1080×1920. That is a false product claim.
//   · "Priorytetowa generacja", "Wsparcie priorytetowe", "Dostęp zespołowy" —
//     nothing in the code implements any of them.
//   · "1 podłączone konto / 3 podłączone konta / indywidualny limit" — the real
//     slot counts are 1 / 8 / 40 (SPEC A3).
//   · "1 gem = 1 wygenerowany edit" — an edit costs 3 gems (SPEC A5).
// Every bullet below maps to something the product actually enforces.
export const pricing = {
  hero: {
    // Canvas 3 łamie nagłówek na dwie linie — druga jest tą, która tłumaczy model.
    // Jedna linia nagłówka Cennika (artboard 01a); akapit i podtytuł wyleciały.
    titleOne: 'Dostęp w abonamencie, praca za tokeny.',
    title: 'Płacisz za dostęp',
    title2: 'i za gemy, którymi płacisz za pracę.',
    sub: 'Subskrypcja daje dostęp do oprogramowania i miesięczną pulę gemów. Gemy zużywasz na konkretne czynności - resztę czasu nic nie kosztują.',
  },
  // Pasek nad katalogiem dla zalogowanego bez planu (projekt 02, stan 2). {gems}/{edits}
  // odmienione w komponencie; liczby z portfela i ceny editu z API.
  signedIn: {
    as: 'Zalogowana jako {email}',
    // Saldo bez nazywania go „bonusem”: konto bez planu może mieć też gemy dokupione,
    // a API nie wydziela kwoty bonusu (R2, 0018).
    gemsEdits: 'Masz {gems} = {edits}.',
    gems: 'Masz {gems}.',
    next: 'Plan to Twój następny krok - pula gemów co miesiąc.',
    toBilling: 'Do Rozliczeń',
  },
  // 390: przełącznik planów + jedna karta + tabela różnic (decyzja 3, bez karuzeli).
  switch: { label: 'Wybierz plan do porównania' },
  diff: {
    heading: 'Czym się różnią',
    gemsPerMonth: 'gemów / mies.',
    edits: '≈ editów',
    accounts: 'konta do publikacji',
    storage: 'miejsce',
    price: 'cena / mies.',
  },
  grossNote: 'Ceny brutto, z VAT',
  // Pigułka stanu sprzedaży — JEDEN tekst na trzech ekranach (Cennik, Gemy i plan,
  // Zamówienie), żeby „wstrzymana” nie zaczęła znaczyć trzech różnych rzeczy.
  salesPaused: 'sprzedaż wstrzymana',
  // Cennik czynności. Ta sekcja istnieje, bo „gem” nic nie znaczy, dopóki nie
  // powiesz, ile kosztuje konkretna rzecz. Liczby (3/2/5) podaje komponent z API.
  gemCost: {
    // Reguła 34 (B-R3): nagłówek nie nazywa już waluty, bo trzy wiersze pod nim
    // nazywają ją same („Edit 1 token”). Podpisy stawek („do 30 sekund, 1080p”,
    // „jeden post na jednym koncie”, „jeden wgrany plik”) wyleciały przez zasadę 8.
    heading: 'Ile to kosztuje',
    render: 'Edit',
    publish: 'Publikacja',
    cut: 'Pocięcie materiału',
    // R1: katalog bez cen czynności — uczciwy brak danych zamiast stałej.
    unavailable: 'Cennik czynności jest chwilowo niedostępny - liczby pojawią się, gdy serwer odpowie.',
  },
  cycle: {
    monthly: 'rozliczane miesięcznie · ceny brutto',
  },
  state: {
    loading: 'Ładowanie cennika…',
    error: 'Nie udało się wczytać cennika.',
    retry: 'Spróbuj ponownie',
  },
  plans: {
    heading: 'Plany',
    perMonth: '/mies.',
    // Wiersz puli w karcie planu: „500 tokenów co miesiąc”. Jednostkę odmienia
    // `tokenWord`, więc tu stoi samo dopełnienie — nigdy przeliczenie na edity.
    poolPerMonth: 'co miesiąc',
    gemsLabel: 'gemów / mies.',
    // --- Karty planów budowane Z DANYCH API (canvas 3). Klucze `starter/pro/max`
    // niżej opisują dzisiejszą, ręcznie wypisaną wersję strony i znikną razem
    // z przepisaniem Pricing.tsx przez P01. Te tutaj są dla wersji z API.
    mostPopular: 'Najczęściej wybierany',
    gemsPerMonth: 'gemów miesięcznie',
    // Odmiana jednostki: 1 konto / 2–4 konta / 5+ kont. Komponent skleja „{n} {jednostka}”.
    accounts_one: 'konto do publikacji',
    accounts_few: 'konta do publikacji',
    accounts_many: 'kont do publikacji',
    storage: 'na Twoje materiały',
    includesPrev: 'Wszystko ze {plan}',
    topupHint: 'Dokup gemów:',
    topupHintMax: 'Tańsze dokupy:',
    // {edits} i {published} przychodzą JUŻ ODMIENIONE z komponentu (editWord()),
    // bo „1 333 edity” i „800 editów” to dwie różne formy w jednym zdaniu.
    equivalence: '{gems} gemów ≈ {edits} albo {published} z publikacją',
    cta: 'Wybierz {plan}',
    starter: {
      name: 'Starter',
      desc: 'Dla artysty, który prowadzi jedno konto.',
      cta: 'Wybierz Starter',
      f1: '1 konto do publikacji',
      f2: 'Generator, paczki klipów, edytor tekstu',
      f3: 'Edity 9:16 w 1080p do pobrania',
      f4: 'Synced lyrics co do sylaby',
      f5: '25 GB na Twoje materiały',
      f6: 'Dokup gemów w każdej chwili',
    },
    pro: {
      name: 'Pro',
      badge: 'Najpopularniejszy',
      desc: 'Dla artysty, który pracuje na kilku kontach naraz.',
      cta: 'Wybierz Pro',
      f1: '8 kont do publikacji',
      f2: 'Wszystko ze Startera',
      f3: 'Autopilot - rozkłada posty po kontach',
      f4: 'Własne materiały cięte na klipy',
      f5: '100 GB na Twoje materiały',
      f6: 'Dokup gemów i slotów',
    },
    max: {
      name: 'Max',
      desc: 'Dla labeli, agencji i managerów prowadzących wielu artystów.',
      cta: 'Wybierz Max',
      f1: '40 kont do publikacji',
      f2: 'Wszystko z Pro',
      f3: 'Wielu artystów w jednym panelu',
      f4: '500 GB na Twoje materiały',
      f5: 'Tańsze dokupy gemów',
      f6: 'Pakiety gemów do 5 000',
    },
  },
  // Darmowy start pod planami (38-R3): działa NIEZALEŻNIE od sprzedaży, więc przy
  // wstrzymanej sprzedaży jest jedyną aktywną akcją strony, a przy włączonej
  // schodzi do małego cichego przycisku obok mikrotekstu.
  freeStart: { cta: 'Zacznij za darmo' },
  guarantee: {
    // To samo zdanie co landing.hero.micro; {n} i {edits} podaje komponent z FREE_START_EDITS
    // (lib/startBonus.ts), nigdy liczba wpisana tutaj. „Bez karty” to przy wstrzymanej
    // sprzedaży jedyny fakt ryzyka przy jedynej żywej akcji strony (film, M1, 27.09).
    freeEdits: 'Bez karty · {n} {edits} gratis',
    cancel: 'Anulujesz sam, online - bez pisania do nas',
    publishing: 'Publikacja na TikToku, Instagramie i YouTube przez oficjalne API',
  },
  // Dokupy jako osobna sekcja — user, któremu zabrakło w połowie miesiąca, ma
  // znaleźć rozwiązanie bez wchodzenia w porównanie planów.
  addons: {
    heading: 'Gdy zabraknie w połowie miesiąca',
    // Nagłówek sekcji w języku minimal + etykieta terminu ważności przy nim.
    headingShort: 'Pakiety tokenów',
    validForLabel: 'ważne 24 miesiące',
    buy: 'Dokup',
    validFor: 'Dokupione gemy są ważne 24 miesiące od zakupu - zostają na koncie także po odnowieniu planu.',
    gems: 'gemów',
    accounts: 'kont do publikacji',
    maxOnly: 'tylko plan Max',
    maxPrice: '{price} w planie Max',
    perMonth: 'miesięcznie, do planu',
    equiv: '≈ {edits}',
  },
  // Trzy rzeczy, o które klient pyta PRZED zapłatą, a nie po. Deskryptor karty
  // jest tu świadomie: nierozpoznane obciążenie to chargeback, nie e-mail.
  trust: {
    heading: 'Zanim zapłacisz',
    // Trzy fakty zamiast trzech kart z tytułem i akapitem (zasada 8).
    factCancel: 'Anulujesz w portalu klienta, dostęp zostaje do końca okresu.',
    factDescriptor: 'Na wyciągu karty: EDIMI.',
    factRefund: 'Nieudany render: tokeny wracają automatycznie.',
    refundsShort: 'Zwroty',
    cancelTitle: 'Anulujesz sam, online',
    cancelBody: 'Rozliczenia, „Zarządzaj płatnością”, portal klienta. Dostęp zostaje do końca opłaconego okresu.',
    descriptorTitle: 'Na wyciągu: EDIMI',
    descriptorBody: 'Tak wygląda obciążenie na karcie - żeby nikt nie zgłaszał go jako nieznane.',
    refundTitle: 'Nieudany render nie kosztuje',
    refundBody: 'Gemy wracają automatycznie - także za post, który nie wyszedł.',
    refundsLink: 'Zwroty i anulowanie',
    sellerLine: 'odstąpienie 14 dni z rozliczeniem proporcjonalnym · sprzedawca: JDG Dominik Łupicki · płatność: Stripe',
  },
  topup: {
    eyebrow: 'Dokupy',
    title: 'Skończyły się gemy? Dokup.',
    lead: 'Jednorazowe paczki gemów - ważne 24 miesiące od zakupu, zostają na koncie także po odnowieniu planu. Największe pakiety są dostępne w planie Max.',
    gems: 'gemów',
    bestBadge: 'Najlepsza cena',
  },
  pay: {
    stripe: 'Stripe',
  },
  cta: {
    title: 'Masz pytania o plany?',
    lead: 'Sprawdź najczęstsze pytania albo po prostu zacznij za darmo.',
    faq: 'Przejdź do FAQ',
  },
}
